import { CellValue } from '../types';
import { RunReading, RunSnapshot, RunStep, printValue } from '../runs';

/**
 * A run as text, for the studio's assistant to bind a report to: what the
 * procedure is, the parameters every step shares, and each step with its
 * readings' identifiers and values. An overview of the whole run, one step
 * whole, or every place some text turns up — each kept to a length a model
 * reads in one go, saying how to get at whatever was left out.
 */

const OVERVIEW_CHARS = 24_000;
const VALUE_CHARS = 40;
const READINGS_PER_STEP = 8;
const LIST_ROWS = 12;
const SEARCH_HITS = 150;

const clip = (text: string, max = VALUE_CHARS) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
const shown = (v: CellValue) => (v === null || v === '' ? '—' : printValue(v));
const lower = (s: string) => s.toLocaleLowerCase();

function stepLabel(step: RunStep): string {
  return [...step.path, step.title].join(' › ');
}

/** What a reading holds, in one short line: its value(s), its list's size, its files. */
function readingValue(r: RunReading): string {
  if (r.choices) return `selected ${r.values.map(shown).join(', ') || 'nothing'}`;
  if (r.records.length > 0) return `${r.records.length} row${r.records.length === 1 ? '' : 's'}`;
  const files = r.attachments?.map((a) => a.fileName) ?? r.files;
  if (files.length > 0) return `file${files.length === 1 ? '' : 's'} ${files.map((f) => clip(f, 30)).join(', ')}`;
  if (r.values.length === 0) return '—';
  return clip(r.values.map(shown).join(', '));
}

function readingLine(r: RunReading): string {
  const title = r.title && r.title !== r.identifier ? ` "${clip(r.title, 60)}"` : '';
  return `${r.identifier}${title} [${r.operationType ?? r.kind}] = ${readingValue(r)}`;
}

function recordColumns(records: Array<Record<string, CellValue>>): string[] {
  const columns: string[] = [];
  for (const record of records) for (const key of Object.keys(record)) if (!columns.includes(key)) columns.push(key);
  return columns;
}

function recordsTable(records: Array<Record<string, CellValue>>, max = LIST_ROWS): string[] {
  const columns = recordColumns(records);
  const lines = [`    ${columns.join(' | ')}`, ...records.slice(0, max).map((r) => `    ${columns.map((c) => clip(shown(r[c] ?? null), 24)).join(' | ')}`)];
  if (records.length > max) lines.push(`    … ${records.length - max} more rows`);
  return lines;
}

function parameterLines(r: RunReading, rows = LIST_ROWS): string[] {
  const title = r.title && r.title !== r.identifier ? ` "${r.title}"` : '';
  if (r.choices) {
    const offered = r.choices.filter((c) => !r.values.includes(c));
    return [`- ${r.identifier}${title}: choice; selected ${r.values.map(shown).join(', ') || 'nothing'}${offered.length ? `; also offered ${offered.map(shown).join(', ')}` : ''}`];
  }
  if (r.records.length > 0) {
    return [`- ${r.identifier}${title}: list of ${r.records.length} row${r.records.length === 1 ? '' : 's'}; columns ${recordColumns(r.records).join(', ')}`, ...recordsTable(r.records, rows)];
  }
  return [`- ${r.identifier}${title} = ${clip(r.values.map(shown).join(', ') || '—', 80)}`];
}

/** Consecutive iterations of one repeating step print as one entry. */
interface StepGroup { first: number; steps: RunStep[] }

function groupSteps(steps: RunStep[]): StepGroup[] {
  const groups: StepGroup[] = [];
  steps.forEach((step, i) => {
    const last = groups[groups.length - 1];
    const prev = last?.steps[last.steps.length - 1];
    if (prev && step.iterationOf && prev.iterationOf === step.iterationOf && prev.title === step.title) last.steps.push(step);
    else groups.push({ first: i + 1, steps: [step] });
  });
  return groups;
}

function statuses(steps: RunStep[]): string {
  const counts = new Map<string, number>();
  for (const s of steps) counts.set(s.status || '—', (counts.get(s.status || '—') ?? 0) + 1);
  return Array.from(counts).map(([status, n]) => (steps.length > 1 ? `${status}×${n}` : status)).join(', ');
}

function header(run: RunSnapshot): string[] {
  const lines = [`Run "${run.title}" (id ${run.id}): ${run.status}, ${run.state}${run.completedAt ? `, completed ${run.completedAt.slice(0, 10)}` : ''}`];
  if (run.sourceTitle) lines.push(`Procedure: ${run.sourceTitle}`);
  if (run.description) lines.push(`Description: ${clip(run.description.replace(/\s+/g, ' '), 300)}`);
  if (run.dut) lines.push(`DUT: ${run.dut.title}${run.dut.partNumber ? `, part ${run.dut.partNumber}` : ''}`);
  if (run.equipment?.length) lines.push(`Equipment: ${run.equipment.map((e) => `${e.name} (${e.identifier})`).join('; ')}`);
  return lines;
}

function overview(run: RunSnapshot): string {
  const lines = header(run);
  const parameters = run.parameters ?? [];
  if (parameters.length > 0) {
    lines.push('', 'Parameters (the procedure\'s own, shared by every step):');
    for (const p of parameters) lines.push(...parameterLines(p));
  }
  lines.push('', `Steps (${run.steps.length}), under the sections they sit in (§): number. title [status], then readings as identifier [kind] = value`);
  const head = lines.join('\n');
  const stepLines: string[] = [];
  let length = head.length;
  let omitted = 0;
  let section: string | null = null;
  for (const group of groupSteps(run.steps)) {
    const [step] = group.steps;
    const numbers = group.steps.length > 1 ? `${group.first}–${group.first + group.steps.length - 1}` : `${group.first}`;
    const iterations = group.steps.length > 1 ? ` (${group.steps.length} iterations; the first shown)` : '';
    const readings = step.readings.slice(0, READINGS_PER_STEP).map((r) => `   - ${readingLine(r)}`);
    if (step.readings.length > READINGS_PER_STEP) readings.push(`   - … ${step.readings.length - READINGS_PER_STEP} more; inspect_run with step ${group.first}`);
    const checks = step.validations?.length ? [`   checks: ${step.validations.map((v) => `${v.passed ? '✓' : '✗'} ${clip(v.title, 50)}`).join('; ')}`] : [];
    const path = step.path.join(' › ');
    const heading = path !== section && path ? [`§ ${path}`] : [];
    const entry = [...heading, `${numbers}. ${step.title}${iterations} [${statuses(group.steps)}]`, ...readings, ...checks].join('\n');
    if (length + entry.length > OVERVIEW_CHARS) {
      omitted += group.steps.length;
      continue;
    }
    stepLines.push(entry);
    length += entry.length + 1;
    section = path;
  }
  if (omitted > 0) stepLines.push(`… ${omitted} more steps left out for length; inspect_run with step or search reaches them.`);
  return [head, ...stepLines].join('\n');
}

function findStepByQuery(run: RunSnapshot, query: string): { step: RunStep; number: number; others: number } | null {
  const trimmed = query.trim().replace(/^#/, '');
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    return n >= 1 && n <= run.steps.length ? { step: run.steps[n - 1], number: n, others: 0 } : null;
  }
  const q = lower(trimmed);
  const matches = run.steps.map((step, i) => ({ step, number: i + 1 })).filter(({ step }) => step.id === trimmed || lower(stepLabel(step)).includes(q));
  return matches.length ? { ...matches[0], others: matches.length - 1 } : null;
}

function stepDetail(run: RunSnapshot, query: string): string {
  const found = findStepByQuery(run, query);
  if (!found) return `No step ${/^\d+$/.test(query.trim()) ? `numbered ${query.trim()}; the run has ${run.steps.length}` : `whose title contains "${query}"`}.`;
  const { step, number, others } = found;
  const byParameterId = new Map(run.steps.flatMap((s) => s.readings).concat(run.parameters ?? []).map((r) => [r.parameterId, r]));
  const lines = [`Step ${number} of ${run.steps.length}: ${stepLabel(step)} — ${step.status || 'no status'}`];
  if (step.iterationOf) lines.push(`Iteration ${(step.iteration ?? 0) + 1} of a repeating step`);
  if (others > 0) lines.push(`(${others} other step${others === 1 ? '' : 's'} also match "${query}"; a step number picks one)`);
  lines.push('', 'Readings:');
  for (const r of step.readings) {
    lines.push(`- ${r.identifier}${r.title && r.title !== r.identifier ? ` "${r.title}"` : ''} [${r.operationType ?? r.kind}]`);
    if (r.choices) lines.push(`    choices ${r.choices.map(shown).join(', ')}; selected ${r.values.map(shown).join(', ') || 'nothing'}`);
    else if (r.values.length > 0) lines.push(`    values ${r.values.map((v) => clip(shown(v), 200)).join(', ')}`);
    if (r.records.length > 0) lines.push(...recordsTable(r.records, 40));
    const files = r.attachments ?? r.files.map((f) => ({ fileName: f, kind: 'other', fileId: '' }));
    if (files.length > 0) lines.push(`    files ${files.map((f) => `${f.fileName} (${f.kind})`).join(', ')}`);
    const source = r.from ? byParameterId.get(r.from) : undefined;
    if (source) lines.push(`    takes its value from ${source.identifier}`);
  }
  if (step.validations?.length) {
    lines.push('', 'Checks:');
    for (const v of step.validations) lines.push(`- ${v.passed ? '✓' : '✗'} ${v.title}: ${clip(v.message.replace(/\s+/g, ' '), 300)}`);
  }
  return lines.join('\n');
}

function searchRun(run: RunSnapshot, text: string): string {
  const q = lower(text.trim());
  if (!q) return 'Search for some text.';
  const hits: string[] = [];
  const matches = (...parts: Array<string | null | undefined>) => parts.some((p) => p && lower(p).includes(q));
  const valuesOf = (r: RunReading) => [...r.values, ...(r.choices ?? []), ...r.records.flatMap((rec) => Object.values(rec))].map((v) => (v === null ? '' : String(v)));
  for (const p of run.parameters ?? []) {
    if (matches(p.identifier, p.title, ...valuesOf(p), ...recordColumns(p.records))) hits.push(`parameter ${readingLine(p)}`);
  }
  run.steps.forEach((step, i) => {
    if (matches(step.title, ...step.path)) hits.push(`step ${i + 1}. ${stepLabel(step)} [${step.status}]`);
    for (const r of step.readings) {
      if (matches(r.identifier, r.title, ...valuesOf(r), ...(r.attachments?.map((a) => a.fileName) ?? r.files))) hits.push(`step ${i + 1} (${clip(step.title, 50)}) › ${readingLine(r)}`);
    }
  });
  if (hits.length === 0) return `Nothing in the run contains "${text}".`;
  const more = hits.length > SEARCH_HITS ? [`… ${hits.length - SEARCH_HITS} more; search for something narrower`] : [];
  return [`${hits.length === 1 ? '1 place contains' : `${hits.length} places contain`} "${text}":`, ...hits.slice(0, SEARCH_HITS), ...more].join('\n');
}

export function describeRun(run: RunSnapshot, input: { step?: string; search?: string } = {}): string {
  if (input.step) return stepDetail(run, input.step);
  if (input.search) return searchRun(run, input.search);
  return overview(run);
}
